import { useEffect, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/ui/button'

/** How long the button says it worked before offering to do it again. */
const COPIED_FOR = 2000

/**
 * Puts one string on the clipboard and says so.
 *
 * Shared because there are two of them now -- a share link and an instance id --
 * and the interesting part is not the copying but what happens when a browser
 * refuses it, which has to be the same answer both times.
 */
export function CopyButton({
  value,
  label,
}: {
  value: string
  /** What is being copied, so the button says which one it is. */
  label: string
}) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => {
      setCopied(false)
    }, COPIED_FOR)
    return () => {
      clearTimeout(timer)
    }
  }, [copied])

  return (
    <Button
      variant="outline"
      size="icon"
      className="size-8"
      aria-label={copied ? t('share.copied') : label}
      onClick={() => {
        void navigator.clipboard
          .writeText(value)
          .then(() => {
            setCopied(true)
          })
          .catch(() => {
            // A browser that refuses the clipboard leaves the value beside this
            // button, on screen and selectable. Saying "copied" when nothing was
            // would be the only way to make that worse.
          })
      }}
    >
      {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
    </Button>
  )
}
